import React, { useState } from 'react';
import { TopBar } from '@/components/TopBar';
import { Page } from '@/components/Page';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useApp } from '@/context/AppContext';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import api, { errMsg } from '@/lib/api';
import { money2 } from '@/lib/helpers';
import { FitText } from '@/components/FitText';
import { toast } from 'sonner';
import { Wallet, Landmark, Smartphone, CreditCard, Loader2, TrendingUp, TrendingDown, ArrowLeftRight, Settings2, ArrowRight, Undo2, FileText, History } from 'lucide-react';

const ICONS = { CASH: Wallet, BANK: Landmark, UPI: Smartphone, CARD: CreditCard };
const SCOPES = [['today', 'Today'], ['month', 'This Month'], ['all', 'All Time']];
const ACCT_KEYS = ['CASH', 'BANK', 'UPI', 'CARD'];
const ACCT_LABEL = { CASH: 'Cash', BANK: 'Bank', UPI: 'UPI', CARD: 'Card' };

export default function AccountsBreakup() {
  const { user } = useApp();
  const isOwner = user?.role === 'OWNER' || user?.owner_access;
  const qc = useQueryClient();
  const [scope, setScope] = useState('today');
  const [openingOpen, setOpeningOpen] = useState(false);
  const [transferOpen, setTransferOpen] = useState(false);
  const [statementOpen, setStatementOpen] = useState(false);
  const { data, isLoading } = useQuery({
    queryKey: ['cash-breakup', scope],
    queryFn: async () => (await api.get(`/reports/cash-breakup?scope=${scope}`)).data.data,
  });
  const accounts = data?.accounts || [];
  const totals = data?.totals || { received: 0, spent: 0, balance: 0 };
  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['cash-breakup'] });
    qc.invalidateQueries({ queryKey: ['acct-transfers'] });
  };

  return (
    <Page>
      <TopBar title="Cash & Bank" showStore={false} />
      <div className="px-4 pt-4">
        <div className="flex flex-wrap items-center gap-2" data-testid="cash-scope">
          {SCOPES.map(([v, l]) => (
            <button key={v} onClick={() => setScope(v)} data-testid={`cash-scope-${v}`}
              className={`rounded-full border px-3 py-1.5 text-xs font-medium ${scope === v ? 'border-primary bg-primary text-primary-foreground' : 'border-border bg-card'}`}>{l}</button>
          ))}
          <div className="ml-auto flex flex-wrap gap-2">
            <Button variant="secondary" size="sm" className="h-9 rounded-lg" onClick={() => setStatementOpen(true)} data-testid="acct-statement-open"><FileText className="mr-1 h-4 w-4" />Statement</Button>
            {isOwner && (
              <>
                <Button variant="secondary" size="sm" className="h-9 rounded-lg" onClick={() => setTransferOpen(true)} data-testid="acct-transfer-open"><ArrowLeftRight className="mr-1 h-4 w-4" />Transfer</Button>
                <Button variant="secondary" size="sm" className="h-9 rounded-lg" onClick={() => setOpeningOpen(true)} data-testid="acct-opening-open"><Settings2 className="mr-1 h-4 w-4" />Opening balances</Button>
              </>
            )}
          </div>
        </div>

        {isLoading ? (
          <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
        ) : (
          <>
            <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
              {accounts.map((a) => {
                const Icon = ICONS[a.key] || Wallet;
                return (
                  <div key={a.key} className="rounded-2xl border border-border bg-card p-4 sc-card" data-testid={`cash-acct-${a.key}`}>
                    <div className="flex items-center gap-2">
                      <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-secondary text-primary"><Icon className="h-4 w-4" /></span>
                      <p className="text-sm font-semibold">{a.label}</p>
                      <span className="ml-auto text-right">
                        <span className="block text-[10px] uppercase tracking-wide text-muted-foreground">Balance</span>
                        <span className={`font-mono text-base font-bold ${a.balance < 0 ? 'text-destructive' : 'text-foreground'}`} data-testid={`cash-balance-${a.key}`}>{money2(a.balance)}</span>
                      </span>
                    </div>
                    <div className="mt-3 grid grid-cols-2 gap-2 text-sm">
                      <div className="rounded-lg bg-emerald-500/10 p-2">
                        <p className="flex items-center gap-1 text-[11px] text-emerald-600"><TrendingUp className="h-3 w-3" />Received</p>
                        <p className="font-mono font-semibold">{money2(a.received)}</p>
                      </div>
                      <div className="rounded-lg bg-destructive/10 p-2">
                        <p className="flex items-center gap-1 text-[11px] text-destructive"><TrendingDown className="h-3 w-3" />Spent</p>
                        <p className="font-mono font-semibold">{money2(a.spent)}</p>
                      </div>
                    </div>
                    <p className="mt-2 text-[11px] text-muted-foreground" data-testid={`cash-meta-${a.key}`}>
                      Opening {money2(a.opening || 0)}
                      {(a.transfer_in || a.transfer_out) ? ` · Transfers +${money2(a.transfer_in || 0)} / -${money2(a.transfer_out || 0)}` : ''}
                    </p>
                  </div>
                );
              })}
            </div>

            <div className="mt-4 rounded-2xl border border-border bg-card p-4" data-testid="cash-totals">
              <p className="mb-2 text-sm font-semibold">All accounts</p>
              <div className="grid grid-cols-3 gap-2 text-center">
                <div className="min-w-0"><p className="text-[11px] text-muted-foreground">Received</p><FitText align="center"><span className="font-mono text-lg font-bold leading-tight text-emerald-600">{money2(totals.received)}</span></FitText></div>
                <div className="min-w-0"><p className="text-[11px] text-muted-foreground">Spent</p><FitText align="center"><span className="font-mono text-lg font-bold leading-tight text-destructive">{money2(totals.spent)}</span></FitText></div>
                <div className="min-w-0"><p className="text-[11px] text-muted-foreground">Net balance</p><FitText align="center"><span className="font-mono text-lg font-bold leading-tight">{money2(totals.balance)}</span></FitText></div>
              </div>
              <p className="mt-2 text-[11px] text-muted-foreground">Balance is all-time money received into each account minus expenses paid from it. Received/Spent reflect the selected period.</p>
            </div>

            <TransferHistory isOwner={isOwner} onChanged={refresh} />
          </>
        )}
      </div>
      {isOwner && <OpeningDialog open={openingOpen} onOpenChange={setOpeningOpen} onSaved={refresh} />}
      {isOwner && <TransferDialog open={transferOpen} onOpenChange={setTransferOpen} onSaved={refresh} />}
      <StatementDialog open={statementOpen} onOpenChange={setStatementOpen} />
    </Page>
  );
}

function OpeningDialog({ open, onOpenChange, onSaved }) {
  const [vals, setVals] = useState({});
  const [saving, setSaving] = useState(false);
  React.useEffect(() => {
    if (!open) return;
    (async () => {
      try { const d = (await api.get('/accounts/opening')).data.data || {}; setVals(d); } catch (e) { /* ignore */ }
    })();
  }, [open]);
  const save = async () => {
    setSaving(true);
    try {
      const payload = {}; ACCT_KEYS.forEach((k) => { payload[k] = Number(vals[k]) || 0; });
      await api.put('/accounts/opening', payload);
      toast.success('Opening balances saved'); onSaved(); onOpenChange(false);
    } catch (e) { toast.error(errMsg(e)); } finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="opening-dialog">
        <DialogHeader><DialogTitle>Opening balances</DialogTitle></DialogHeader>
        <p className="text-xs text-muted-foreground">Set the starting balance for each account so running totals match your bank/cash on hand.</p>
        <div className="mt-2 grid grid-cols-2 gap-3">
          {ACCT_KEYS.map((k) => (
            <div key={k}>
              <Label className="text-xs">{ACCT_LABEL[k]}</Label>
              <Input inputMode="decimal" value={vals[k] ?? ''} onChange={(e) => setVals({ ...vals, [k]: e.target.value })} placeholder="0" className="mt-1 h-11 rounded-xl" data-testid={`opening-${k}`} />
            </div>
          ))}
        </div>
        <DialogFooter>
          <Button className="h-11 w-full rounded-xl" onClick={save} disabled={saving} data-testid="opening-save">{saving ? 'Saving…' : 'Save opening balances'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function TransferDialog({ open, onOpenChange, onSaved }) {
  const [form, setForm] = useState({ from_account: 'CASH', to_account: 'BANK', amount: '', note: '' });
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (form.from_account === form.to_account) { toast.error('Pick two different accounts.'); return; }
    if (!(Number(form.amount) > 0)) { toast.error('Enter an amount.'); return; }
    setSaving(true);
    try {
      await api.post('/accounts/transfer', { ...form, amount: Number(form.amount) });
      toast.success('Transfer recorded'); onSaved(); onOpenChange(false);
      setForm({ from_account: 'CASH', to_account: 'BANK', amount: '', note: '' });
    } catch (e) { toast.error(errMsg(e)); } finally { setSaving(false); }
  };
  const pick = (field, k) => setForm({ ...form, [field]: k });
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="transfer-dialog">
        <DialogHeader><DialogTitle>Record transfer</DialogTitle></DialogHeader>
        <div className="flex items-center justify-between gap-2">
          <AcctPicker label="From" value={form.from_account} onPick={(k) => pick('from_account', k)} idp="from" />
          <ArrowRight className="mt-5 h-4 w-4 shrink-0 text-muted-foreground" />
          <AcctPicker label="To" value={form.to_account} onPick={(k) => pick('to_account', k)} idp="to" />
        </div>
        <div className="mt-2">
          <Label className="text-xs">Amount</Label>
          <Input inputMode="decimal" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} placeholder="0" className="mt-1 h-11 rounded-xl" data-testid="transfer-amount" />
        </div>
        <div className="mt-2">
          <Label className="text-xs">Note (optional)</Label>
          <Input value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} placeholder="e.g. Cash deposited to bank" className="mt-1 h-11 rounded-xl" data-testid="transfer-note" />
        </div>
        <DialogFooter>
          <Button className="h-11 w-full rounded-xl" onClick={save} disabled={saving} data-testid="transfer-save">{saving ? 'Saving…' : 'Record transfer'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const AcctPicker = ({ label, value, onPick, idp }) => (
  <div className="flex-1">
    <Label className="text-xs">{label}</Label>
    <div className="mt-1 grid grid-cols-2 gap-1">
      {ACCT_KEYS.map((k) => (
        <button key={k} type="button" onClick={() => onPick(k)} data-testid={`transfer-${idp}-${k}`}
          className={`rounded-lg border px-2 py-1.5 text-[11px] font-medium ${value === k ? 'border-primary bg-primary text-primary-foreground' : 'border-border bg-card'}`}>{ACCT_LABEL[k]}</button>
      ))}
    </div>
  </div>
);

function TransferHistory({ isOwner, onChanged }) {
  const { data, isLoading } = useQuery({
    queryKey: ['acct-transfers'],
    queryFn: async () => (await api.get('/accounts/transfers?limit=20')).data.data,
  });
  const [undoId, setUndoId] = useState(null);
  const [busy, setBusy] = useState(false);
  const rows = data || [];
  const doUndo = async () => {
    if (!undoId) return;
    setBusy(true);
    try {
      await api.delete(`/accounts/transfer/${undoId}`);
      toast.success('Transfer undone'); setUndoId(null); onChanged();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  if (isLoading || !rows.length) return null;
  return (
    <div className="mt-4 rounded-2xl border border-border bg-card p-4" data-testid="transfer-history">
      <div className="flex items-center gap-2">
        <History className="h-4 w-4 text-primary" />
        <p className="text-sm font-semibold">Recent transfers</p>
      </div>
      <div className="mt-2 divide-y divide-border">
        {rows.map((t) => (
          <div key={t.id} className="flex items-center gap-3 py-2.5" data-testid={`transfer-row-${t.id}`}>
            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-secondary text-primary"><ArrowLeftRight className="h-4 w-4" /></span>
            <div className="min-w-0 flex-1">
              <p className="flex items-center gap-1 text-sm font-medium">
                {ACCT_LABEL[t.from_account] || t.from_account}
                <ArrowRight className="h-3 w-3 text-muted-foreground" />
                {ACCT_LABEL[t.to_account] || t.to_account}
              </p>
              <p className="truncate text-[11px] text-muted-foreground">
                {(t.date || t.created_at || '').slice(0, 10)}{t.note ? ` \u00b7 ${t.note}` : ''}{t.created_by ? ` \u00b7 ${t.created_by}` : ''}
              </p>
            </div>
            <span className="font-mono text-sm font-semibold" data-testid={`transfer-amt-${t.id}`}>{money2(t.amount)}</span>
            {isOwner && (
              <Button variant="ghost" size="sm" className="h-8 rounded-lg px-2 text-destructive hover:text-destructive" onClick={() => setUndoId(t.id)} data-testid={`transfer-undo-${t.id}`}>
                <Undo2 className="mr-1 h-4 w-4" />Undo
              </Button>
            )}
          </div>
        ))}
      </div>
      <AlertDialog open={!!undoId} onOpenChange={(o) => !o && setUndoId(null)}>
        <AlertDialogContent data-testid="transfer-undo-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Undo this transfer?</AlertDialogTitle>
            <AlertDialogDescription>This removes the transfer and restores both account balances.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="transfer-undo-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doUndo} disabled={busy} data-testid="transfer-undo-confirm-btn">{busy ? 'Undoing\u2026' : 'Undo transfer'}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

const _todayISO = () => new Date().toISOString().slice(0, 10);
const _monthStartISO = () => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10); };
const TYPE_LABEL = { SALE: 'Sale', EXPENSE: 'Expense', TRANSFER_IN: 'Transfer in', TRANSFER_OUT: 'Transfer out', OPENING: 'Opening' };

function StatementDialog({ open, onOpenChange }) {
  const [account, setAccount] = useState('CASH');
  const [from, setFrom] = useState(_monthStartISO());
  const [to, setTo] = useState(_todayISO());
  const [loading, setLoading] = useState(false);
  const [stmt, setStmt] = useState(null);
  const load = async (acc = account, f = from, t = to) => {
    setLoading(true);
    try {
      const d = (await api.get(`/accounts/statement?account=${acc}&from=${f}&to=${t}`)).data.data;
      setStmt(d);
    } catch (e) { toast.error(errMsg(e)); } finally { setLoading(false); }
  };
  React.useEffect(() => { if (open) load(); /* eslint-disable-next-line */ }, [open]);
  const pickAccount = (k) => { setAccount(k); load(k, from, to); };
  const rows = stmt?.rows || [];
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[88vh] overflow-y-auto" data-testid="statement-dialog">
        <DialogHeader><DialogTitle>Account statement</DialogTitle></DialogHeader>
        <div className="grid grid-cols-4 gap-1" data-testid="statement-account">
          {ACCT_KEYS.map((k) => (
            <button key={k} type="button" onClick={() => pickAccount(k)} data-testid={`statement-acct-${k}`}
              className={`rounded-lg border px-2 py-1.5 text-[11px] font-medium ${account === k ? 'border-primary bg-primary text-primary-foreground' : 'border-border bg-card'}`}>{ACCT_LABEL[k]}</button>
          ))}
        </div>
        <div className="mt-2 flex items-end gap-2">
          <div className="flex-1"><Label className="text-[11px] text-muted-foreground">From</Label><Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="mt-1 h-10 rounded-lg" data-testid="statement-from" /></div>
          <div className="flex-1"><Label className="text-[11px] text-muted-foreground">To</Label><Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="mt-1 h-10 rounded-lg" data-testid="statement-to" /></div>
          <Button className="h-10 rounded-lg" onClick={() => load()} disabled={loading} data-testid="statement-apply">{loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'View'}</Button>
        </div>

        {stmt && !loading && (
          <>
            <div className="mt-3 grid grid-cols-2 gap-2 text-sm sm:grid-cols-4" data-testid="statement-summary">
              <div className="min-w-0 rounded-lg bg-secondary p-2"><p className="text-[10px] uppercase text-muted-foreground">Opening</p><FitText data-testid="statement-opening"><span className="font-mono font-semibold leading-tight">{money2(stmt.opening)}</span></FitText></div>
              <div className="min-w-0 rounded-lg bg-emerald-500/10 p-2"><p className="text-[10px] uppercase text-emerald-600">Money in</p><FitText><span className="font-mono font-semibold leading-tight">{money2((stmt.totals?.in || 0) + (stmt.totals?.transfer_in || 0))}</span></FitText></div>
              <div className="min-w-0 rounded-lg bg-destructive/10 p-2"><p className="text-[10px] uppercase text-destructive">Money out</p><FitText><span className="font-mono font-semibold leading-tight">{money2((stmt.totals?.out || 0) + (stmt.totals?.transfer_out || 0))}</span></FitText></div>
              <div className="min-w-0 rounded-lg bg-primary/10 p-2"><p className="text-[10px] uppercase text-primary">Closing</p><FitText data-testid="statement-closing"><span className="font-mono font-semibold leading-tight">{money2(stmt.closing)}</span></FitText></div>
            </div>
            <div className="mt-3 divide-y divide-border" data-testid="statement-rows">
              {rows.length === 0 ? (
                <p className="py-6 text-center text-sm text-muted-foreground" data-testid="statement-empty">No transactions in this period.</p>
              ) : rows.map((r, i) => (
                <div key={i} className="flex items-center gap-3 py-2" data-testid={`statement-row-${i}`}>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium">{TYPE_LABEL[r.type] || r.type}</p>
                    <p className="truncate text-[11px] text-muted-foreground">{(r.date || '').slice(0, 10)}{r.label ? ` \u00b7 ${r.label}` : ''}</p>
                  </div>
                  <span className={`font-mono text-sm font-semibold ${r.direction === 'in' ? 'text-emerald-600' : 'text-destructive'}`}>
                    {r.direction === 'in' ? '+' : '\u2212'} {money2(r.amount)}
                  </span>
                </div>
              ))}
            </div>
          </>
        )}
        {loading && <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div>}
      </DialogContent>
    </Dialog>
  );
}
